import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { 
  Building2, Calendar, Users, TrendingUp, RefreshCw, HelpCircle,
  LogIn, LogOut, DoorClosed
} from 'lucide-react';
import { 
  format, parseISO, addDays, isAfter, isBefore, startOfToday, 
  startOfMonth, endOfMonth, getDaysInMonth, differenceInDays, isToday,
  startOfDay, endOfDay, startOfYear, subMonths,
  startOfWeek, endOfWeek, addWeeks
} from 'date-fns';
import { dateLocales } from '../i18n';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import LoadingSpinner from '../components/LoadingSpinner';
import Tooltip from '../components/Tooltip';
import { useNotification } from '../contexts/NotificationContext';
import BookingDetailsModal from '../components/BookingDetailsModal';
import ActivityLogViewer from '../components/ActivityLogViewer';
import StatCard from '../components/StatCard';
import OccupancyHeatMap from '../components/OccupancyHeatMap';
import TrendChart from '../components/TrendChart';
import { DashboardStats, Booking } from '../types/dashboard';

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const { user, userRole } = useAuth();
  const { addNotification } = useNotification();
  const [stats, setStats] = useState<DashboardStats>({
    totalProperties: 0,
    totalBookings: 0,
    totalUsers: 0,
    occupancyRate: 0,
    todayArrivals: 0,
    todayDepartures: 0,
    currentStays: 0,
    averageNights: 0
  });
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [activityLogs, setActivityLogs] = useState<any[]>([]);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [occupancyData, setOccupancyData] = useState<{ date: string; occupancyRate: number }[]>([]);
  const [yearlyTrends, setYearlyTrends] = useState<{ date: string; bookings: number; occupancyRate: number }[]>([]);
  const [calendarView, setCalendarView] = useState<'month' | 'fourWeeks' | 'week'>('fourWeeks');

  const fetchDashboardData = useCallback(async () => {
    try {
      setError(null);
      setIsRefreshing(true);

      // Récupérer les propriétés
      const propertiesResponse = await supabase
        .from('properties')
        .select('*');

      if (propertiesResponse.error) throw propertiesResponse.error;
      const properties = propertiesResponse.data || [];

      // Pour les propriétaires, filtrer leurs propriétés
      const userProperties = userRole === 'owner' 
        ? properties.filter(p => p.owner_id === user?.id)
        : properties;

      const startDate = startOfMonth(new Date());
      const endDate = endOfMonth(new Date());
      const daysInMonth = getDaysInMonth(startDate);

      // Récupérer les réservations
      const bookingsResponse = await supabase
        .from('bookings')
        .select(`
          id,
          property:properties(id, name, address),
          guest_first_name,
          guest_last_name,
          guest_count,
          check_in_date,
          check_out_date,
          cleaning_service,
          bed_sheets_service,
          guest_email,
          guest_phone,
          door_code,
          comments
        `);

      if (bookingsResponse.error) throw bookingsResponse.error;
      const bookingsData = bookingsResponse.data || [];

      // Calculer le taux d'occupation
      const occupiedNightsByProperty = new Map<string, Set<string>>();

      bookingsData.forEach(booking => {
        // Vérifier si la propriété appartient à l'utilisateur si c'est un propriétaire
        if (userRole === 'owner' && !userProperties.some(p => p.id === booking.property.id)) {
          return;
        }

        const checkIn = parseISO(booking.check_in_date);
        const checkOut = parseISO(booking.check_out_date);
        
        // Pour chaque jour entre check-in et check-out (excluant le jour de départ)
        let currentDate = startOfDay(checkIn);
        while (currentDate < checkOut) {
          // Ne compter que les jours dans le mois en cours
          if (currentDate >= startDate && currentDate <= endDate) {
            const dateStr = format(currentDate, 'yyyy-MM-dd');
            const propertyId = booking.property.id;
            
            if (!occupiedNightsByProperty.has(propertyId)) {
              occupiedNightsByProperty.set(propertyId, new Set());
            }
            occupiedNightsByProperty.get(propertyId)?.add(dateStr);
          }
          currentDate = addDays(currentDate, 1);
        }
      });

      // Calculer le taux d'occupation global
      let totalOccupiedNights = 0;
      occupiedNightsByProperty.forEach(nights => {
        totalOccupiedNights += nights.size;
      });

      // Le nombre total de nuits disponibles dépend du nombre de propriétés
      const totalAvailableNights = userProperties.length * daysInMonth;
      const occupancyRate = totalAvailableNights > 0 
        ? (totalOccupiedNights / totalAvailableNights) * 100 
        : 0;

      // Calculer les autres statistiques
      const todayStart = startOfDay(new Date());
      const todayEnd = endOfDay(new Date());
      
      const todayArrivals = bookingsData.filter(b => isToday(parseISO(b.check_in_date))).length;
      const todayDepartures = bookingsData.filter(b => isToday(parseISO(b.check_out_date))).length;
      
      // Filtrer les séjours en cours
      const currentStaysBookings = bookingsData.filter(b => {
        const today = startOfToday();
        const checkIn = parseISO(b.check_in_date);
        const checkOut = parseISO(b.check_out_date);
        return isAfter(today, checkIn) && isBefore(today, checkOut);
      });

      const currentStays = currentStaysBookings.length;

      // Calculer la moyenne des nuits par réservation
      const totalNights = bookingsData.reduce((sum, booking) => {
        return sum + differenceInDays(parseISO(booking.check_out_date), parseISO(booking.check_in_date));
      }, 0);

      setStats({
        totalProperties: userProperties.length,
        totalBookings: bookingsData.length,
        totalUsers: 0,
        occupancyRate,
        todayArrivals,
        todayDepartures,
        currentStays,
        averageNights: bookingsData.length > 0 ? totalNights / bookingsData.length : 0
      });

      setBookings(bookingsData);

      // Récupérer les logs d'activité
      const { data: logsData } = await supabase
        .from('activity_logs')
        .select(`
          id,
          user:profiles(first_name, last_name),
          action,
          entity_type,
          entity_id,
          details,
          created_at
        `)
        .order('created_at', { ascending: false })
        .limit(10);

      if (logsData) {
        setActivityLogs(logsData);
      }

      // Calculer les données d'occupation pour le heat map
      const daysInCurrentMonth = Array.from(
        { length: daysInMonth },
        (_, i) => addDays(startDate, i)
      );

      const occupancyByDay = daysInCurrentMonth.map(day => {
        const occupiedProperties = bookingsData.filter(booking => {
          const checkIn = parseISO(booking.check_in_date);
          const checkOut = parseISO(booking.check_out_date);
          // Ne pas compter le jour de départ
          return !isAfter(checkIn, day) && isBefore(day, checkOut);
        }).length;

        return {
          date: format(day, 'yyyy-MM-dd'),
          occupancyRate: (occupiedProperties / (userProperties.length || 1)) * 100
        };
      });

      setOccupancyData(occupancyByDay);

      // Calculer les tendances annuelles
      const monthlyTrends = Array.from(
        { length: 12 },
        (_, i) => subMonths(new Date(), i)
      ).reverse().map(month => {
        const monthStart = startOfMonth(month);
        const monthEnd = endOfMonth(month);
        const monthlyBookings = bookingsData.filter(booking => {
          const checkIn = parseISO(booking.check_in_date);
          return !isAfter(checkIn, monthEnd) && !isBefore(checkIn, monthStart);
        });

        const occupiedDays = new Set();
        monthlyBookings.forEach(booking => {
          const checkIn = parseISO(booking.check_in_date);
          const checkOut = parseISO(booking.check_out_date);
          let currentDate = checkIn;
          while (currentDate < checkOut) {
            if (!isAfter(currentDate, monthEnd) && !isBefore(currentDate, monthStart)) {
              occupiedDays.add(format(currentDate, 'yyyy-MM-dd'));
            }
            currentDate = addDays(currentDate, 1);
          }
        });

        return {
          date: format(month, 'yyyy-MM-dd'),
          bookings: monthlyBookings.length,
          occupancyRate: (occupiedDays.size / (getDaysInMonth(month) * (userProperties.length || 1))) * 100
        };
      });

      setYearlyTrends(monthlyTrends);

    } catch (error: any) {
      console.error('Error fetching dashboard data:', error);
      const errorMessage = error.message || 'Une erreur est survenue lors du chargement des données';
      setError(errorMessage);
      addNotification('error', errorMessage);
      setStats({
        totalProperties: 0,
        totalBookings: 0,
        totalUsers: 0,
        occupancyRate: 0,
        todayArrivals: 0,
        todayDepartures: 0,
        currentStays: 0,
        averageNights: 0
      });
      setBookings([]);
      setActivityLogs([]);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [addNotification, userRole, user?.id]);

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  function getEventColor(booking: Booking) {
    if (booking.cleaning_service && booking.bed_sheets_service) {
      return '#9333ea'; // purple-600
    } else if (booking.cleaning_service) {
      return '#ea580c'; // orange-600
    } else if (booking.bed_sheets_service) {
      return '#2563eb'; // blue-600
    }
    return '#16a34a'; // green-600
  }

  function getEventContent(eventInfo: any) {
    const booking = eventInfo.event.extendedProps;
    return (
      <div className="h-full" style={{ backgroundColor: eventInfo.event.backgroundColor }}>
        <div className="p-1 text-white h-full">
          <div className="font-semibold text-sm truncate">
            {booking.property.name}
          </div>
          <div className="text-sm truncate">
            {booking.guest_first_name} {booking.guest_last_name}
          </div>
          <div className="text-xs flex items-center gap-1">
            <span className="flex items-center">
              <Users size={12} className="mr-1" />
              {booking.guest_count}
            </span>
            {booking.cleaning_service && (
              <span className="bg-white/20 px-1 rounded">🧹</span>
            )}
            {booking.bed_sheets_service && (
              <span className="bg-white/20 px-1 rounded">🛏️</span>
            )}
          </div>
        </div>
      </div>
    );
  }

  function getCalendarDuration() {
    const today = new Date();
    let start, end;

    switch (calendarView) {
      case 'week':
        start = startOfWeek(today, { weekStartsOn: 1 });
        end = endOfWeek(today, { weekStartsOn: 1 });
        break;
      case 'fourWeeks':
        start = startOfWeek(today, { weekStartsOn: 1 });
        end = endOfWeek(addWeeks(today, 3), { weekStartsOn: 1 });
        break;
      default:
        start = startOfMonth(today);
        end = endOfMonth(today);
    }

    return { start, end };
  }

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6 text-center">
        <div className="bg-red-50 text-red-800 p-4 rounded-lg inline-block">
          <p className="font-medium">{t('common.error')}</p>
          <p className="text-sm mt-1">{error}</p>
          <button
            onClick={fetchDashboardData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 mx-auto"
          >
            <RefreshCw size={20} className={isRefreshing ? 'animate-spin' : ''} />
            {t('common.refresh')}
          </button>
        </div>
      </div>
    );
  }

  const { start, end } = getCalendarDuration();

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          {t('dashboard.title')}
          <Tooltip content={t('dashboard.overview')}>
            <HelpCircle className="w-5 h-5 text-gray-400" />
          </Tooltip>
        </h1>
        <button
          onClick={fetchDashboardData}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300"
          disabled={isRefreshing}
        >
          <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
          <span className="text-sm">{t('common.refresh')}</span>
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title={t('dashboard.stats.todayArrivals')}
          value={stats.todayArrivals}
          icon={LogIn}
          color="bg-green-500"
          bookings={bookings.filter(b => isToday(parseISO(b.check_in_date)))}
        />
        <StatCard
          title={t('dashboard.stats.todayDepartures')}
          value={stats.todayDepartures}
          icon={LogOut}
          color="bg-red-500"
          bookings={bookings.filter(b => isToday(parseISO(b.check_out_date)))}
        />
        <StatCard
          title={t('dashboard.stats.currentStays')}
          value={stats.currentStays}
          icon={DoorClosed}
          color="bg-blue-500"
          bookings={bookings.filter(b => {
            const today = startOfToday();
            const checkIn = parseISO(b.check_in_date);
            const checkOut = parseISO(b.check_out_date);
            return isAfter(today, checkIn) && isBefore(today, checkOut);
          })}
        />
        <StatCard
          title={t('dashboard.stats.occupancyRate')}
          value={`${Math.round(stats.occupancyRate)}%`}
          icon={TrendingUp}
          color="bg-purple-500"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <TrendChart
            data={yearlyTrends}
            title="Évolution sur 12 mois"
          />
        </div>

        <div>
          <OccupancyHeatMap
            data={occupancyData}
            year={new Date().getFullYear()}
            month={new Date().getMonth()}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('bookings.title')}
            </h2>
            <div className="flex gap-2">
              <button
                onClick={() => setCalendarView('week')}
                className={`px-3 py-1 rounded-lg text-sm ${
                  calendarView === 'week'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('common.week')}
              </button>
              <button
                onClick={() => setCalendarView('fourWeeks')}
                className={`px-3 py-1 rounded-lg text-sm ${
                  calendarView === 'fourWeeks'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                4 {t('common.week')}s
              </button>
            </div>
          </div>

          <div className="min-h-[500px] sm:min-h-[600px]">
            <FullCalendar
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridFourWeeks"
              views={{
                dayGridFourWeeks: {
                  type: 'dayGrid',
                  duration: { weeks: 4 },
                  buttonText: '4 semaines'
                }
              }}
              locale={dateLocales[i18n.language]}
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridFourWeeks,dayGridWeek'
              }}
              buttonText={{
                today: t('common.today'),
                week: t('common.week')
              }}
              events={bookings.map(booking => ({
                id: booking.id,
                title: '',
                start: `${booking.check_in_date}T16:00:00`,
                end: `${booking.check_out_date}T11:00:00`,
                backgroundColor: getEventColor(booking),
                borderColor: 'transparent',
                extendedProps: booking,
                display: 'block',
                className: 'rounded-lg overflow-hidden hover:opacity-90 transition-opacity'
              }))}
              eventContent={getEventContent}
              height="auto"
              dayMaxEvents={3}
              moreLinkText={t('common.more')}
              firstDay={1}
              weekNumbers={true}
              displayEventEnd={true}
              nextDayThreshold="00:00:00"
              slotMinTime="00:00:00"
              slotMaxTime="24:00:00"
              allDaySlot={false}
              eventClick={({ event }) => setSelectedBooking(event.extendedProps)}
            />
          </div>

          <div className="mt-4 flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded bg-green-600"></span>
              <span className="text-gray-700 dark:text-gray-300">
                {t('bookings.filters.allServices')}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded bg-orange-600"></span>
              <span className="text-gray-700 dark:text-gray-300">
                {t('bookings.filters.cleaning')}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded bg-blue-600"></span>
              <span className="text-gray-700 dark:text-gray-300">
                {t('bookings.filters.bedSheets')}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded bg-purple-600"></span>
              <span className="text-gray-700 dark:text-gray-300">
                {t('bookings.filters.both')}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            {t('dashboard.activity.title')}
            <Tooltip content={t('dashboard.activity.title')}>
              <HelpCircle className="w-4 h-4 text-gray-400" />
            </Tooltip>
          </h2>
          <ActivityLogViewer logs={activityLogs} />
        </div>
      </div>

      {selectedBooking && (
        <BookingDetailsModal
          booking={selectedBooking}
          onClose={() => setSelectedBooking(null)}
        />
      )}
    </div>
  );
}